import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

const WORKER_URL = 'https://sync.rocketcitynerd.com';

interface WorkerStatus {
  status: string;
  timestamp: string;
  lastSync?: {
    launches?: string;
    news?: string;
    solarFlares?: string;
    huntsvilleEvents?: string;
  };
}

interface SyncResult {
  success: boolean;
  count?: number;
  error?: string;
}

/**
 * Hook to check the status of the data sync worker
 */
export function useWorkerStatus() {
  return useQuery<WorkerStatus, Error>({
    queryKey: ['worker-status'],
    queryFn: async () => {
      const response = await fetch(`${WORKER_URL}/status`);
      if (!response.ok) {
        throw new Error(`Failed to get worker status: ${response.status}`);
      }
      return response.json();
    },
    staleTime: 30 * 1000, // 30 seconds
    refetchInterval: 60 * 1000, // 1 minute
  });
}

/**
 * Hook to manually trigger sync operations via the worker
 */
export function useManualSync() {
  const queryClient = useQueryClient();

  const syncAll = useMutation<SyncResult, Error>({
    mutationFn: async () => {
      const response = await fetch(`${WORKER_URL}/sync/all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Sync failed: ${response.status} ${errorData}`);
      }
      
      return response.json();
    },
    onSuccess: () => {
      // Invalidate all event and news queries
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['allEvents'] });
      queryClient.invalidateQueries({ queryKey: ['news'] });
      queryClient.invalidateQueries({ queryKey: ['worker-status'] });
    },
  });

  const syncLaunches = useMutation<SyncResult, Error>({
    mutationFn: async () => {
      const response = await fetch(`${WORKER_URL}/sync/launches`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Launch sync failed: ${response.status} ${errorData}`);
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['allEvents'] });
      queryClient.invalidateQueries({ queryKey: ['worker-status'] });
    },
  });

  const syncNews = useMutation<SyncResult, Error>({
    mutationFn: async () => {
      const response = await fetch(`${WORKER_URL}/sync/news`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`News sync failed: ${response.status} ${errorData}`);
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['news'] });
      queryClient.invalidateQueries({ queryKey: ['worker-status'] });
    },
  });

  const syncSolarFlares = useMutation<SyncResult, Error>({
    mutationFn: async () => {
      const response = await fetch(`${WORKER_URL}/sync/solar-flares`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Solar flares sync failed: ${response.status} ${errorData}`);
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['allEvents'] });
      queryClient.invalidateQueries({ queryKey: ['worker-status'] });
    },
  });

  const syncHuntsvilleEvents = useMutation<SyncResult, Error>({
    mutationFn: async () => {
      const response = await fetch(`${WORKER_URL}/sync/huntsville-events`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) {
        const errorData = await response.text();
        throw new Error(`Huntsville events sync failed: ${response.status} ${errorData}`);
      }
      
      return response.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['events'] });
      queryClient.invalidateQueries({ queryKey: ['allEvents'] });
      queryClient.invalidateQueries({ queryKey: ['worker-status'] });
    },
  });

  return {
    syncAll,
    syncLaunches,
    syncNews,
    syncSolarFlares,
    syncHuntsvilleEvents,
  };
}
